/**
 * Function to get the shortened names or initials of input tabs names.
 * @param {Array<String>} tabNames - An array containing names of tabs.
 *
 * @returns {(Map<String, String>|null)} - A map containing the original tab name as key and the generated shortened name as value.
 */
function getTabsShortNames(tabNames) {
	if (!tabNames) {
		return null;
	}
	const map = new Map();

	for (const tabName of tabNames) {
		const words = tabName.split(" ");
		let initials = "";

		if (words.length === 1) {
			initials = tabName.slice(0, 2).toUpperCase();
		} else {
			for (const word of words) {
				initials += word.slice(0, 1).toUpperCase();
			}
		}

		if (tabName === tabName.toUpperCase()) {
			initials = tabName;
		}

		map.set(tabName, initials);
	}

	return map;
}

/**
 * Retrieves a Map object which contains the OEM name as the key and
 * the OEM reference as the value.
 *
 * @returns {Map<String, String>} - Returns a map where the key is the oemName and the value is the oemReference.
 */
function getOEMShortNames(locationOEMs = []) {
	const map = new Map();
	const sanitizedOEMs = getSanitatedLocationOEMs(locationOEMs);
	sanitizedOEMs.forEach((oem) => map.set(oem.oemName, oem.oemReference));
	return map;
}

function getBucketIngestionFileNameKeys(tabsNameMap, oneStepSelect, currentTab) {
	const tabShortName = tabsNameMap.get(currentTab) || null;
	const commissionNameKeys = ["commissionCycleTitle", "commissionCycleYear"];
	const commonKeys = ["franchise", ...commissionNameKeys];

	const tabShortNameMapping = Array.from(tabsNameMap.entries()).map(([key, value]) => {
		const acc = {};
		if (key === currentTab && oneStepSelect.includes(currentTab)) {
			acc[value] = [value.toLowerCase().trim() + "Prefix", ...commissionNameKeys];
		} else {
			acc[value] = [value.toLowerCase().trim() + "Prefix", ...commonKeys];
		}
		return acc;
	});

	const pathStrings = tabShortNameMapping.find((item) => tabShortName in item);

	return pathStrings[tabShortName] || null;
}

const getOEMFranchisesNames = (franchises = []) => {
	return franchises.reduce((names, definition) => {
		let labels = [];
		if (definition?.franchise) {
			labels = [definition.franchise];
		}
		if (Boolean(definition?.isCombined) && definition?.groups?.length) {
			labels = definition.groups.map((group) => group?.franchiseName || "") || [];
		}
		return [...names, ...labels];
	}, []);
};

const getOEMFranchisesLabels = (locationOEMs = []) =>
	locationOEMs?.reduce((franchises, oem) => [...franchises, ...getOEMFranchisesNames(oem?.franchises || [])], []);

const generateRandomChars = (characters = 8, useDefault = false) => {
	if (useDefault) {
		return "Unknown";
	}
	let result = "";
	const charactersSet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
	const charsLength = charactersSet.length;
	while (characters) {
		result += charactersSet.charAt(Math.floor(Math.random() * charsLength));
		characters--;
	}
	return result;
};

/**
 * OEM sanitation for correct oemNames based on franchises.
 * @function getSanitatedLocationOEMs
 * @description Function to sanitize a list of OEM's and correct their oemName's based on franchises.
 * @param {Array} locationOEMs - The list of OEM's to sanitize.
 * @returns {Array} - The sanitized OEM's list.
 */
const getSanitatedLocationOEMs = (locationOEMs = []) => {
	const uniqueNames = new Map();
	const uniqueLabels = new Map(); // Used to store and compare if franchises labels are unique

	locationOEMs?.forEach((oem) => {
		const isPresent = uniqueNames.get(oem.oemReference);
		uniqueNames.set(oem.oemReference, { isPresent: Boolean(isPresent) });
	});

	const getFranchisesLabel = (franchises = [], makeDistinct = false, joiner = "") => {
		if (!franchises?.length) {
			return generateRandomChars(8, !makeDistinct);
		}
		const name = franchises.map((item) => item.charAt(0) || "");
		let result = name.join(joiner)?.trim()?.toUpperCase();

		if (makeDistinct) {
			const reference = result;
			const lastFranchise = [...franchises].pop() || generateRandomChars(8);
			let position = 1;
			while (reference === result) {
				const addChart = lastFranchise.charAt(position);
				result = `${result}${addChart}`;
				position++;
			}
		}

		return result;
	};

	return locationOEMs?.map((oem) => {
		const definition = uniqueNames?.get(oem.oemReference);
		const isMultiple = definition.isPresent;
		let oemDisplayName = oem.oemName;

		if (isMultiple) {
			const franchises = getOEMFranchisesNames(oem?.franchises || []);
			let franchiseLabel = getFranchisesLabel(franchises);

			// Check if this label already exists
			const isUsedLabel = uniqueLabels.get(franchiseLabel);
			if (isUsedLabel) {
				franchiseLabel = getFranchisesLabel(franchises, true);
			}
			uniqueLabels.set(franchiseLabel, true);
			oemDisplayName = franchiseLabel ? `${oem.oemName} (${franchiseLabel})` : oem?.oemName || "";
		}

		return { ...oem, oemDisplayName };
	});
};

const getOEMParameters = ({ oemReference, dealerCodeFS, locationOEMs = [], params = ["oemName", "oemDisplayName"] }) => {
	if (!oemReference || !dealerCodeFS || !locationOEMs?.length || !params?.length) {
		return null;
	}
	const oem = locationOEMs.find((item) => item.oemReference === oemReference && item.dealerCodeFS === dealerCodeFS);
	if (!oem) {
		return null;
	}
	return params.reduce((acc, parameter) => {
		acc[parameter] = oem?.[parameter] || null;
		return acc;
	}, {});
};

/**
 * Normalize a value and transform it into upper or lower case.
 * @function textNormalize
 * @description Function to normalize a value converting it to string and transform it into upper or lower case.
 * @param {any} value - The specific value to normalize.
 * @param {string} transform - The specific transform case. Ex.: "uppercase", "lowercase" or "default". Default set to "uppercase".
 * @returns {String} - The normalized value.
 */
const textNormalize = (value, transform = "uppercase") => {
	if (!value) {
		return value;
	}
	let text = String(value);
	switch (transform) {
		case "uppercase":
			text = text.toUpperCase();
			break;
		case "lowercase":
			text = text.toLowerCase();
			break;
		default:
			break;
	}
	return text.trim();
};

/**
 * Check if a franchise is included inside a list of franchises.
 * @function isFranchiseIncluded
 * @description Function to check if a franchise is included inside a list of franchises.
 * @param {Array} franchises - The list of franchises to search in.
 * @param {string} franchise - The franchise to search for.
 * @returns {Boolean} True if the franchise was found, otherwise returns false.
 */
const isFranchiseIncluded = (franchises = [], franchise = "") => {
	if (!franchises?.length || !franchise) {
		return false;
	}
	const franchiseToCompare = textNormalize(franchise);
	return franchises.some((item) => {
		let isGrouped = false;
		if (item?.isCombined) {
			isGrouped = item?.groups?.some((element) => textNormalize(element?.franchiseName) === franchiseToCompare);
		}
		return textNormalize(item.franchise) === franchiseToCompare || isGrouped;
	});
};

const getFranchiseOEM = (locationOEMs = [], franchise = "") => {
	if (!locationOEMs?.length || !franchise) {
		return null;
	}
	return locationOEMs.find((item) => isFranchiseIncluded(item?.franchises, franchise));
};

const getMappedOEMFranchises = (locationOEMs = [], franchises = []) => {
	return franchises.map((franchise) => {
		const oem = getFranchiseOEM(locationOEMs, franchise);
		return {
			franchise,
			oemReference: oem?.oemReference ?? "",
		};
	});
};

const getFranchiseOEMReference = (args) => {
	const { franchise, oemReference = "", locationOEMs = [] } = args ?? {};
	if (!oemReference) {
		const oem = getFranchiseOEM(locationOEMs, franchise);
		return oem?.oemReference ?? "";
	}
	return oemReference;
};

const getFranchiseOEMProgramVersion = (args) => {
	const { version = "" } = args || {};
	const oemReference = getFranchiseOEMReference(args);
	return {
		oemReference,
		version,
	};
};

export {
	getTabsShortNames,
	getOEMShortNames,
	getBucketIngestionFileNameKeys,
	getMappedOEMFranchises,
	getFranchiseOEMProgramVersion,
	getOEMFranchisesLabels,
	getOEMParameters,
	getSanitatedLocationOEMs,
	getFranchiseOEM,
	getFranchiseOEMReference,
};
